import { createContext, useContext, useState, type ReactNode } from 'react'
import { gameServer } from '../game/client'

// Class codes are checked by the game server (gakushiori.com/live), so no code ships in the site's files.
// A right code earns a signed pass for one section, kept on this device until the school year ends.

const STORAGE_KEY = 'gs.passes'

export interface ClassPass {
  section: string
  name: string
  pass: string
}

export type UnlockResult = 'ok' | 'wrong' | 'wait' | 'unreachable'

interface ClassCodeContextValue {
  isUnlocked: boolean
  passes: ClassPass[]
  unlock: (code: string) => Promise<{ result: UnlockResult; section?: string }>
  forget: (section: string) => void
  lock: () => void
  add: (p: ClassPass) => void // a pass from elsewhere (the teacher's test class), shown on this device's site next
  redeem: (invite: string) => Promise<UnlockResult | 'expired'> // an invite link for friends and family: the Guests class
}

function load(): ClassPass[] {
  try {
    const v = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]')
    return Array.isArray(v) ? v.filter((p) => p && typeof p.pass === 'string' && typeof p.section === 'string') : []
  } catch {
    return []
  }
}

function save(passes: ClassPass[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(passes))
  } catch {
    /* private window: the code is asked again next visit */
  }
}

const ClassCodeContext = createContext<ClassCodeContextValue | undefined>(undefined)

export function ClassCodeProvider({ children }: { children: ReactNode }) {
  const [passes, setPasses] = useState<ClassPass[]>(load)

  const update = (next: ClassPass[]) => {
    setPasses(next)
    save(next)
  }

  const unlock = async (code: string): Promise<{ result: UnlockResult; section?: string }> => {
    const base = await gameServer()
    if (!base) return { result: 'unreachable' }
    try {
      const r = await fetch(`${base}/access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      })
      if (r.status === 401) return { result: 'wrong' }
      if (r.status === 429) return { result: 'wait' }
      if (!r.ok) return { result: 'unreachable' }
      const p = (await r.json()) as ClassPass
      update([...load().filter((x) => x.section !== p.section), { section: p.section, name: p.name, pass: p.pass }])
      return { result: 'ok', section: p.section }
    } catch {
      return { result: 'unreachable' }
    }
  }

  const forget = (section: string) => {
    update(load().filter((p) => p.section !== section))
    try {
      localStorage.removeItem(`gs.board.${section}`)
    } catch {
      /* nothing cached */
    }
  }
  const redeem = async (invite: string): Promise<UnlockResult | 'expired'> => {
    const base = await gameServer()
    if (!base) return 'unreachable'
    try {
      const r = await fetch(`${base}/access/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ invite }),
      })
      if (r.status === 410) return 'expired'
      if (r.status === 401) return 'wrong'
      if (r.status === 429) return 'wait'
      if (!r.ok) return 'unreachable'
      add((await r.json()) as ClassPass)
      return 'ok'
    } catch {
      return 'unreachable'
    }
  }
  const lock = () => update([])
  const add = (p: ClassPass) => {
    update([...load().filter((x) => x.section !== p.section), p])
    try {
      localStorage.setItem('gs.class', p.section)
    } catch {
      /* the class switcher still has it */
    }
  }

  return (
    <ClassCodeContext.Provider value={{ isUnlocked: passes.length > 0, passes, unlock, forget, lock, add, redeem }}>
      {children}
    </ClassCodeContext.Provider>
  )
}

export function useClassCode() {
  const ctx = useContext(ClassCodeContext)
  if (!ctx) throw new Error('useClassCode must be used within ClassCodeProvider')
  return ctx
}
